// v2 donor-portal wave-2 API client (CHG-1519 / ITER-3638).
//
// Consumes the v1 portal backend at /api/v1/my-giving/* (portal.py).
// Pages are v2-canonical (mount at /me/onboarding, /me/recurring/:id,
// /me/pledges/:id) but reuse the v1 endpoints rather than minting a
// new /api/v1/me/* surface for the same data — the v1 portal routes
// are stable, well-tested, and don't need a sibling.
//
// Types are minimal projections of v1's PortalRecurring /
// PortalPledgeDetail / OnboardingStatus shapes (web/dashboard/src/
// types/index.ts) — only the fields the v2 pages render. Adding a
// field downstream is a one-line append here. When v2 builds its own
// /api/v1/me/* equivalents, swap the URL prefix; the types stay.

import { apiClient } from "./client";

// ─── Onboarding ──────────────────────────────────────────────────────

export interface OnboardingStatus {
  completed: boolean;
  onboarding_completed_at?: string;
}

export async function fetchOnboardingStatus(): Promise<OnboardingStatus> {
  const r = await apiClient().get<OnboardingStatus>(
    "/my-giving/onboarding-status",
  );
  return r.data;
}

export async function completeOnboarding(): Promise<{ message: string }> {
  const r = await apiClient().post<{ message: string }>(
    "/my-giving/complete-onboarding",
  );
  return r.data;
}

// ─── Recurring detail ────────────────────────────────────────────────

export interface PortalRecurring {
  id: string;
  organization_id: string;
  org_name: string;
  org_logo_url?: string;
  designation_id?: string;
  designation_name?: string;
  amount: number;
  amount_minor_units?: number;
  currency: string;
  frequency: string;
  status: string;
  next_charge_at?: string;
  created_at?: string;
  schedule_label?: string | null;
  provider_key?: string | null;
}

export async function fetchRecurringDonation(
  id: string,
): Promise<PortalRecurring> {
  const r = await apiClient().get<PortalRecurring>(
    `/my-giving/recurring-donations/${id}`,
  );
  return r.data;
}

export async function pauseRecurring(id: string): Promise<PortalRecurring> {
  const r = await apiClient().post<PortalRecurring>(
    `/my-giving/recurring-donations/${id}/pause`,
  );
  return r.data;
}

export async function resumeRecurring(id: string): Promise<PortalRecurring> {
  const r = await apiClient().post<PortalRecurring>(
    `/my-giving/recurring-donations/${id}/resume`,
  );
  return r.data;
}

export async function cancelRecurring(id: string): Promise<PortalRecurring> {
  const r = await apiClient().post<PortalRecurring>(
    `/my-giving/recurring-donations/${id}/cancel`,
  );
  return r.data;
}

// ─── Pledge detail ───────────────────────────────────────────────────

export interface PortalPledge {
  id: string;
  organization_id: string;
  org_name: string;
  org_logo_url?: string;
  designation_id?: string;
  campaign_id?: string;
  campaign_name?: string;
  session_id?: string;
  session_name?: string;
  total_amount_minor_units: number;
  fulfilled_minor_units: number;
  outstanding_minor_units: number;
  currency: string;
  status: string;
  frequency?: string;
  start_date?: string;
  due_date?: string;
  // PLG-R17 / F19 — donor-facing reminder cadence surfaces. Both come from
  // the portal pledge read (PortalPledgeRead, CHG-2331). next_reminder_at:
  // the schedule-forward target the C3 reminder cron advances each cycle
  // (NULL for terminal pledges / no upcoming window). schedule_label: a human
  // cadence label derived from schedule_expression (e.g. "every other week",
  // "2nd Friday"); NULL for coarse-frequency-only pledges, where `frequency`
  // is the label. Reminders are notifications only — they never charge.
  next_reminder_at?: string | null;
  schedule_label?: string | null;
}

export interface PortalPledgePayment {
  id: string;
  paid_at: string;
  amount_minor_units: number;
  donation_id?: string | null;
}

export interface PortalPledgeDetail {
  pledge: PortalPledge;
  payments: PortalPledgePayment[];
}

export interface PortalPledgeTimelineEntry {
  at: string;
  kind: string;
  note?: string | null;
  actor?: string | null;
}

export interface PortalPledgePaymentUrlResponse {
  payment_url: string;
}

export async function fetchPledgeDetail(
  id: string,
): Promise<PortalPledgeDetail> {
  const r = await apiClient().get<PortalPledgeDetail>(
    `/my-giving/pledges/${id}`,
  );
  return r.data;
}

export async function payPledge(
  id: string,
): Promise<PortalPledgePaymentUrlResponse> {
  const r = await apiClient().post<PortalPledgePaymentUrlResponse>(
    `/my-giving/pledges/${id}/pay`,
  );
  return r.data;
}

export async function fetchPledgeTimeline(
  id: string,
): Promise<PortalPledgeTimelineEntry[]> {
  const r = await apiClient().get<PortalPledgeTimelineEntry[]>(
    `/my-giving/pledges/${id}/timeline`,
  );
  return r.data;
}

export async function cancelPledge(id: string): Promise<PortalPledge> {
  const r = await apiClient().post<PortalPledge>(
    `/my-giving/pledges/${id}/cancel`,
  );
  return r.data;
}
